import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github-dark.css";
// AnimatedHelper REMOVED (2026-02-21) — user directive: "No need for
// cartoon helper if we have videos". Videos now live exclusively in
// the /videos section of the Dashboard.
import InvisibleTeacher from "./InvisibleTeacher";
import LessonDeepDive from "./LessonDeepDive";
import { isYoungLearnerGrade } from "../utils/gradeUtils";
import { clearLessonProgress } from "../utils/lessonProgress";
import useScreenWakeLock from "../hooks/useScreenWakeLock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Map a K-12 grade level to the Motion Studio art tier. Higher-grade
 *  lessons request a more mature, editorial illustration style. */
const _gradeTierFor = (g) => {
  if (!g) return "6-8";
  const s = String(g).toLowerCase();
  if (["k", "1", "2", "3", "4", "5", "k-2", "3-5"].includes(s)) return "K-5";
  if (["6", "7", "8", "6-8"].includes(s)) return "6-8";
  if (["9", "10", "11", "12", "9-12"].includes(s)) return "9-12";
  if (s.includes("seminary") || s.includes("ordination")) return "Seminary";
  return "6-8";
};

function LessonWorkspace({ 
  subjectId, 
  subjectName,
  subjectIcon,
  gradeLevel, 
  lessonNumber,
  lessonTitle,
  onBack,
  onTakeQuiz,
  onContentLoaded,
  lessonsList,
  onNextLesson,
  onPrevLesson,
  // 2026-02 Continue-button fix — parent renderer tells us which
  // section to route back to from the Dashboard "Pick up where you
  // left off" card. Defaults to "k12-program" so K-12 lessons keep
  // working without any caller changes; UnifiedCourses passes "courses"
  // so the Continue button lands in the Courses section.
  sectionContext = "k12-program",
}) {
  // Determine next sequential lesson (if any) based on current lesson number
  const nextLesson = (() => {
    if (!lessonsList || !Array.isArray(lessonsList) || lessonsList.length === 0) return null;
    const getNum = (l) => l?.number ?? l?.lesson_number;
    const idx = lessonsList.findIndex(l => getNum(l) === lessonNumber);
    if (idx === -1 || idx >= lessonsList.length - 1) return null;
    return lessonsList[idx + 1];
  })();

  // Determine previous sequential lesson (if any)
  const prevLesson = (() => {
    if (!lessonsList || !Array.isArray(lessonsList) || lessonsList.length === 0) return null;
    const getNum = (l) => l?.number ?? l?.lesson_number;
    const idx = lessonsList.findIndex(l => getNum(l) === lessonNumber);
    if (idx <= 0) return null;
    return lessonsList[idx - 1];
  })();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("content");
  const [notes, setNotes] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [showVocabQuiz, setShowVocabQuiz] = useState(false);
  const [vocabAnswers, setVocabAnswers] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  // Set to true once the InvisibleTeacher finishes reading every section.
  // Triggers a celebratory banner + auto-scroll to the "Next Lesson →" CTA
  // so the student doesn't have to scroll through the lesson body to find it.
  const [lessonComplete, setLessonComplete] = useState(false);
  // Restart-key: bumped each time the student taps "🔄 Restart Lesson".
  // Becomes the React `key` on <InvisibleTeacher/> so the teacher
  // unmounts + remounts after we've cleared its localStorage progress —
  // a clean "start from section 1" with no stale state.
  const [restartKey, setRestartKey] = useState(0);

  // 2026-02-21 mandate: "Ensure screen doesn't fall asleep during
  // lessons." Screen Wake Lock stays active for the entire time this
  // workspace is mounted; no-ops silently on browsers that don't
  // support the API.
  useScreenWakeLock(true);

  // Reset complete-state when the user navigates to a different lesson
  useEffect(() => {
    setLessonComplete(false);
  }, [lessonNumber]);

  // Restart helper — wipes saved progress for THIS lesson, resets the
  // dashboard "resume" pointer if it points here, and triggers a
  // teacher remount via the restartKey.
  const handleRestartLesson = () => {
    clearLessonProgress(lessonTitle);
    setLessonComplete(false);
    setRestartKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Dashboard "Pick up where you left off" pointer ──────────────────
  // When the student lands on a lesson, drop a global "last lesson" record
  // into localStorage. The InvisibleTeacher updates the section index as
  // the student progresses; here we just guarantee the lesson coordinates
  // are written even before any audio starts (so a student who opens a
  // lesson and immediately leaves still gets a resume card).
  useEffect(() => {
    if (!content || !lessonTitle) return;
    try {
      const existing = JSON.parse(localStorage.getItem("ylc_last_lesson") || "null");
      const lessonKey = String(lessonTitle).trim().toLowerCase().replace(/\s+/g, "_");
      // Preserve the existing sectionIndex if it points at the same lesson;
      // otherwise reset to 0 (student is starting a fresh lesson).
      const sectionIndex =
        existing && existing.lessonKey === lessonKey
          ? existing.sectionIndex || 0
          : 0;
      const totalSections =
        (content.sections ? content.sections.length : 0) + 1; // +1 for intro
      localStorage.setItem(
        "ylc_last_lesson",
        JSON.stringify({
          section: sectionContext,
          lessonKey,
          lessonTitle,
          gradeLevel,
          subject: subjectName,
          subjectId,
          lessonNumber,
          sectionIndex,
          totalSections,
          updatedAt: Date.now(),
        })
      );
    } catch (e) {
      /* ignore */
    }
  }, [content, lessonTitle, lessonNumber, gradeLevel, subjectName]);
  
  // Content reference for lesson sections
  const lessonContainerRef = useRef(null);

  const downloadPDF = async () => {
    if (pdfLoading || !content) return;
    setPdfLoading(true);
    // Ensure the "Lesson Content" tab is the one being rendered, so the PDF
    // captures Introduction + all Sections + Key Points + Biblical Connection.
    setActiveSection("content");
    try {
      // Wait one animation frame for the tab switch to paint
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 300)));
      const { exportLessonPDF } = await import("../utils/pdfExport");
      const el = lessonContainerRef.current;
      if (!el) return;
      await exportLessonPDF({
        title: lessonTitle || content?.title || `Lesson ${lessonNumber}`,
        subtitle: `${subjectName ? subjectName + " · " : ""}Grade ${gradeLevel} · Lesson ${lessonNumber}`,
        bodyNode: el,
        gradeLevel,
      });
    } catch (err) {
      console.error("PDF download failed:", err);
      alert("Sorry — we couldn't build the PDF for this lesson. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    fetchLessonContent();
    loadSavedNotes();
    loadProgress();
  }, [subjectId, gradeLevel, lessonNumber]);

  const fetchLessonContent = async () => {
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/lesson/content/${subjectId}/${gradeLevel}/${lessonNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const contentData = response.data.content;
        
        // Validate content is not empty or placeholder
        if (!contentData || 
            !contentData.introduction || 
            contentData.introduction.includes("being prepared") ||
            !contentData.sections || 
            contentData.sections.length === 0 ||
            (contentData.sections.length === 1 && contentData.sections[0].title === "Loading...")) {
          // Content is empty or placeholder - set error for regeneration
          setError("Lesson content is being generated. Please click 'Refresh Content' to try again.");
          setContent(null);
        } else {
          setContent(contentData);
          // Notify parent component that content is loaded
          onContentLoaded?.({ content: contentData });
        }
      } else {
        setError("Failed to load lesson content");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error loading lesson content");
    } finally {
      setLoading(false);
    }
  };
  
  const regenerateContent = async () => {
    // Clear cached content by requesting regeneration
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      // Delete cached content first
      await axios.delete(
        `${API}/lesson/content/${subjectId}/${gradeLevel}/${lessonNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {}); // Ignore if delete fails
      
      // Now fetch fresh content
      await fetchLessonContent();
    } catch (err) {
      setError("Error regenerating content. Please try again.");
      setLoading(false);
    }
  };

  const loadSavedNotes = () => {
    const savedNotes = localStorage.getItem(`lesson_notes_${subjectId}_${gradeLevel}_${lessonNumber}`);
    if (savedNotes) {
      setNotes(savedNotes);
    }
  };

  const loadProgress = () => {
    const progress = localStorage.getItem(`lesson_progress_${subjectId}_${gradeLevel}_${lessonNumber}`);
    if (progress) {
      setCompletedSections(JSON.parse(progress));
    }
  };

  const saveNotes = () => {
    localStorage.setItem(`lesson_notes_${subjectId}_${gradeLevel}_${lessonNumber}`, notes);
    setNoteSaved(true);
    setTimeout(() => setNoteSaved(false), 2000);
  };

  const toggleSectionComplete = (sectionIndex) => {
    const newCompleted = completedSections.includes(sectionIndex)
      ? completedSections.filter(i => i !== sectionIndex)
      : [...completedSections, sectionIndex];
    
    setCompletedSections(newCompleted);
    localStorage.setItem(
      `lesson_progress_${subjectId}_${gradeLevel}_${lessonNumber}`,
      JSON.stringify(newCompleted)
    );
  };

  const getProgressPercentage = () => {
    if (!content?.sections) return 0;
    const totalSections = content.sections.length + 1; // +1 for introduction
    return Math.round((completedSections.length / totalSections) * 100);
  };

  const checkVocabAnswer = (term, userAnswer) => {
    setVocabAnswers(prev => ({
      ...prev,
      [term]: userAnswer.toLowerCase().trim()
    }));
  };

  if (loading) {
    return (
      <div className="card" data-testid="lesson-workspace-loading">
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ fontSize: "4rem", marginBottom: "20px" }}>📖</div>
          <h3 style={{ color: "#FFD700", marginBottom: "15px" }}>
            Loading Lesson Material...
          </h3>
          <p style={{ color: "#aaa" }}>
            Preparing your study workspace
          </p>
          <div className="spinner" style={{ margin: "20px auto" }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" data-testid="lesson-workspace-error">
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: "4rem", marginBottom: "20px" }}>📖</div>
          <h3 style={{ color: "#FFD700", marginBottom: "15px" }}>
            Lesson Content Issue
          </h3>
          <div className="alert alert-error" style={{ marginBottom: "20px" }}>{error}</div>
          <p style={{ color: "#aaa", marginBottom: "20px" }}>
            The lesson content may still be generating or needs to be refreshed.
          </p>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
            <button 
              onClick={regenerateContent}
              data-testid="regenerate-content-btn"
              style={{
                background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                color: "#000",
                padding: "12px 25px",
                fontWeight: "bold"
              }}
            >
              🔄 Refresh Content
            </button>
            <button 
              onClick={onBack} 
              style={{ 
                padding: "12px 25px",
                background: "#333"
              }}
            >
              ← Back to Curriculum
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        ref={lessonContainerRef}
        data-testid="lesson-workspace" 
        style={{ maxWidth: "1200px", margin: "0 auto", paddingBottom: "110px" }}
      >
        {/* Header */}
        <div className="card" style={{ 
          background: "linear-gradient(135deg, #1a1a2a 0%, #2a1a1a 100%)",
          borderTop: "4px solid #FFD700"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "15px" }}>
            <div>
              <button 
                onClick={onBack}
                style={{ 
                  background: "transparent", 
                  border: "none", 
                  color: "#aaa", 
                  padding: "5px 0",
                  marginBottom: "10px",
                  cursor: "pointer",
                  fontSize: "0.9rem"
                }}
              >
                ← Back to Curriculum
              </button>
              <h2 style={{ color: "#FFD700", margin: "0 0 10px 0", display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "2rem" }}>{subjectIcon || "📖"}</span>
                Lesson {lessonNumber}: {lessonTitle || content?.title || "Study Material"}
              </h2>
              <p style={{ color: "#aaa", margin: 0 }}>
                {subjectName} • {gradeLevel} Level
              </p>
            </div>
            
            {/* Progress Circle */}
            <div style={{ 
            textAlign: "center",
            background: "#0a0a0a",
            padding: "15px 25px",
            borderRadius: "12px"
          }}>
            <div style={{ 
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              background: `conic-gradient(#90EE90 ${getProgressPercentage() * 3.6}deg, #333 0deg)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 8px"
            }}>
              <div style={{
                width: "55px",
                height: "55px",
                borderRadius: "50%",
                background: "#0a0a0a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                color: "#90EE90"
              }}>
                {getProgressPercentage()}%
              </div>
            </div>
            <div style={{ fontSize: "0.8rem", color: "#aaa" }}>Progress</div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="card" style={{ padding: "15px", background: "#0a0a0a" }}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
          {[
            { id: "content", label: "📚 Lesson Content" },
            { id: "activities", label: "🎮 Activities" },
            { id: "vocabulary", label: "📝 Vocabulary" },
            { id: "resources", label: "🔗 Resources" },
            { id: "notes", label: "✏️ My Notes" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              data-testid={`tab-${tab.id}`}
              style={{
                padding: "12px 24px",
                background: activeSection === tab.id ? "#FFD700" : "#222",
                color: activeSection === tab.id ? "#000" : "#eee",
                border: activeSection === tab.id ? "none" : "1px solid #333",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: activeSection === tab.id ? "bold" : "normal",
                marginTop: 0,
                fontSize: "0.95rem",
                transition: "all 0.2s ease"
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      {activeSection === "content" && (
        <div data-testid="lesson-content-section">
          {/* Cartoon helper removed 2026-02-21 — videos live in
              Dashboard → 🎬 Videos section. Kept the Deep Dive
              below for scripture/vocab/application drill-down. */}
          {/* Empty Content Fallback */}
          {(!content?.introduction && (!content?.sections || content.sections.length === 0)) && (
            <div className="card" style={{ 
              background: "#2a1a1a",
              borderLeft: "4px solid #FFD700",
              textAlign: "center",
              padding: "40px 20px"
            }}>
              <div style={{ fontSize: "3rem", marginBottom: "15px" }}>📚</div>
              <h3 style={{ color: "#FFD700", marginBottom: "15px" }}>Content Loading...</h3>
              <p style={{ color: "#aaa", marginBottom: "20px" }}>
                The lesson content is still being generated or needs to be refreshed.
              </p>
              <button
                onClick={regenerateContent}
                data-testid="regenerate-content-inline-btn"
                style={{
                  background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                  color: "#000",
                  padding: "12px 25px",
                  fontWeight: "bold"
                }}
              >
                🔄 Refresh Content
              </button>
            </div>
          )}
          
          {/* Introduction */}
          {content?.introduction && (
            <div 
              className="card" 
              data-testid="lesson-introduction"
              style={{ 
                background: "#1a2a1a",
                borderLeft: "4px solid #90EE90"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <h3 style={{ color: "#90EE90", margin: "0 0 15px 0" }}>
                  📖 Introduction
                </h3>
                <button
                  onClick={() => toggleSectionComplete(0)}
                  style={{
                    background: completedSections.includes(0) ? "#90EE90" : "#333",
                    color: completedSections.includes(0) ? "#000" : "#aaa",
                    padding: "5px 12px",
                    borderRadius: "20px",
                    fontSize: "0.8rem",
                    marginTop: 0,
                    border: "none",
                    cursor: "pointer"
                  }}
                >
                  {completedSections.includes(0) ? "✓ Completed" : "Mark Complete"}
                </button>
              </div>
              <div 
                className="lesson-section-content"
                style={{ 
                  lineHeight: "1.9", 
                  color: "#eee",
                  fontSize: "1.05rem"
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>{content.introduction}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* AI Deep Dive — scriptures, vocabulary, application (cache-first) */}
          {content?.introduction && (
            <LessonDeepDive
              lessonId={`${subjectId}-g${gradeLevel}-l${lessonNumber}`}
              lessonTitle={lessonTitle || `Lesson ${lessonNumber}`}
              subject={subjectName || subjectId}
              gradeLevel={gradeLevel}
            />
          )}

          {/* Main Content Sections */}
          {content?.sections?.map((section, index) => (
            <div 
              key={index} 
              className="card"
              data-testid={`lesson-section-${index}`}
              style={{
                background: completedSections.includes(index + 1) ? "#1a2a1a" : "#1a1a1a",
                borderLeft: `4px solid ${completedSections.includes(index + 1) ? "#90EE90" : "#87CEEB"}`
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px" }}>
                <h3 style={{ 
                  color: completedSections.includes(index + 1) ? "#90EE90" : "#87CEEB", 
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}>
                  <span style={{
                    background: "#FFD700",
                    color: "#000",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.9rem",
                    fontWeight: "bold"
                  }}>
                    {index + 1}
                  </span>
                  {section.title}
                </h3>
                <button
                  onClick={() => toggleSectionComplete(index + 1)}
                  style={{
                    background: completedSections.includes(index + 1) ? "#90EE90" : "#333",
                    color: completedSections.includes(index + 1) ? "#000" : "#aaa",
                    padding: "5px 12px",
                    borderRadius: "20px",
                    fontSize: "0.8rem",
                    marginTop: 0,
                    border: "none",
                    cursor: "pointer"
                  }}
                >
                  {completedSections.includes(index + 1) ? "✓ Completed" : "Mark Complete"}
                </button>
              </div>
              <div 
                className="lesson-section-content"
                style={{ 
                  lineHeight: "1.9", 
                  color: "#ddd",
                  fontSize: "1.02rem"
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>{section.content}</ReactMarkdown>
              </div>
            </div>
          ))}

          {/* Key Points Summary */}
          {content?.key_points?.length > 0 && (
            <div className="card" style={{
              background: "#1a1a3a",
              borderLeft: "4px solid #FFD700"
            }}>
              <h3 style={{ color: "#FFD700", margin: "0 0 15px 0" }}>
                🎯 Key Points to Remember
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {content.key_points.map((point, index) => (
                  <div 
                    key={index}
                    style={{
                      background: "#0a0a1a",
                      padding: "12px 15px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px"
                    }}
                  >
                    <span style={{ 
                      color: "#FFD700",
                      fontWeight: "bold",
                      minWidth: "24px"
                    }}>
                      {index + 1}.
                    </span>
                    <span style={{ color: "#eee", lineHeight: "1.6" }}>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Biblical Connection */}
          {content?.biblical_connection && (
            <div className="card" style={{
              background: "linear-gradient(135deg, #2a1a1a 0%, #1a1a2a 100%)",
              borderLeft: "4px solid #FFD700"
            }}>
              <h3 style={{ color: "#FFD700", margin: "0 0 15px 0" }}>
                ✝️ Biblical Connection
              </h3>
              <div style={{ 
                lineHeight: "1.9", 
                color: "#eee",
                fontStyle: "italic",
                fontSize: "1.05rem",
                padding: "10px 15px",
                background: "#0a0a0a",
                borderRadius: "8px"
              }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>{content.biblical_connection}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Activities Section */}
      {activeSection === "activities" && (
        <div data-testid="activities-section">
          <div className="card" style={{ background: "#1a2a1a", borderLeft: "4px solid #90EE90" }}>
            <h3 style={{ color: "#90EE90", margin: "0 0 20px 0" }}>
              🎮 Learning Activities
            </h3>
            
            {content?.activities?.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                {content.activities.map((activity, index) => (
                  <div 
                    key={index}
                    style={{
                      background: "#0a0a0a",
                      padding: "20px",
                      borderRadius: "12px",
                      border: "1px solid #333"
                    }}
                  >
                    <h4 style={{ color: "#FFD700", margin: "0 0 10px 0", display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{
                        background: "#90EE90",
                        color: "#000",
                        width: "30px",
                        height: "30px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.9rem"
                      }}>
                        {index + 1}
                      </span>
                      {activity.title}
                    </h4>
                    <p style={{ color: "#aaa", margin: 0, lineHeight: "1.7" }}>
                      {activity.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: "#aaa", textAlign: "center" }}>
                No activities available for this lesson.
              </p>
            )}
          </div>

          {/* Discussion Questions */}
          {content?.discussion_questions?.length > 0 && (
            <div className="card" style={{ background: "#2a2a1a", borderLeft: "4px solid #FFD700" }}>
              <h3 style={{ color: "#FFD700", margin: "0 0 20px 0" }}>
                💬 Discussion Questions
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {content.discussion_questions.map((question, index) => (
                  <div 
                    key={index}
                    style={{
                      background: "#0a0a0a",
                      padding: "15px 20px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px"
                    }}
                  >
                    <span style={{ 
                      color: "#FFD700",
                      fontWeight: "bold",
                      fontSize: "1.1rem"
                    }}>
                      Q{index + 1}:
                    </span>
                    <span style={{ color: "#eee", lineHeight: "1.6" }}>{question}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vocabulary Section */}
      {activeSection === "vocabulary" && (
        <div data-testid="vocabulary-section">
          <div className="card" style={{ background: "#2a1a2a", borderLeft: "4px solid #DDA0DD" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ color: "#DDA0DD", margin: 0 }}>
                📝 Vocabulary Terms
              </h3>
              {content?.vocabulary?.length > 0 && (
                <button
                  onClick={() => setShowVocabQuiz(!showVocabQuiz)}
                  style={{
                    background: showVocabQuiz ? "#DDA0DD" : "#333",
                    color: showVocabQuiz ? "#000" : "#DDA0DD",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    marginTop: 0,
                    fontSize: "0.9rem"
                  }}
                >
                  {showVocabQuiz ? "📖 View Definitions" : "🧠 Practice Quiz"}
                </button>
              )}
            </div>
            
            {content?.vocabulary?.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {content.vocabulary.map((vocab, index) => (
                  <div 
                    key={index}
                    style={{
                      background: "#1a1a1a",
                      padding: "15px 20px",
                      borderRadius: "8px"
                    }}
                  >
                    {showVocabQuiz ? (
                      <div>
                        <div style={{ color: "#DDA0DD", fontWeight: "bold", marginBottom: "10px" }}>
                          Define: {vocab.term}
                        </div>
                        <input
                          type="text"
                          placeholder="Type your answer..."
                          value={vocabAnswers[vocab.term] || ""}
                          onChange={(e) => checkVocabAnswer(vocab.term, e.target.value)}
                          style={{
                            width: "100%",
                            padding: "10px 15px",
                            background: "#0a0a0a",
                            border: "1px solid #333",
                            borderRadius: "6px",
                            color: "#eee",
                            marginBottom: "8px"
                          }}
                        />
                        {vocabAnswers[vocab.term] && (
                          <div style={{ 
                            color: "#aaa", 
                            fontSize: "0.9rem",
                            padding: "8px",
                            background: "#0a0a0a",
                            borderRadius: "4px"
                          }}>
                            <strong>Answer:</strong> {vocab.definition}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <strong style={{ color: "#DDA0DD" }}>{vocab.term}:</strong>{" "}
                        <span style={{ color: "#ddd" }}>{vocab.definition}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: "#aaa", textAlign: "center" }}>
                No vocabulary terms for this lesson.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Resources Section */}
      {activeSection === "resources" && (
        <div data-testid="resources-section">
          <div className="card" style={{ background: "#1a1a2a", borderLeft: "4px solid #87CEEB" }}>
            <h3 style={{ color: "#87CEEB", margin: "0 0 20px 0" }}>
              🔗 Free Study Resources
            </h3>
            
            {content?.resources?.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {content.resources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: "#0a0a0a",
                      padding: "15px 20px",
                      borderRadius: "8px",
                      textDecoration: "none",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1px solid #333",
                      transition: "border-color 0.2s"
                    }}
                    onMouseOver={(e) => e.currentTarget.style.borderColor = "#87CEEB"}
                    onMouseOut={(e) => e.currentTarget.style.borderColor = "#333"}
                  >
                    <div>
                      <div style={{ color: "#87CEEB", fontWeight: "bold" }}>
                        {resource.title}
                      </div>
                      <div style={{ color: "#999", fontSize: "0.85rem", marginTop: "4px" }}>
                        {resource.url}
                      </div>
                    </div>
                    <span style={{
                      background: "#1a2a3a",
                      color: "#87CEEB",
                      padding: "4px 12px",
                      borderRadius: "20px",
                      fontSize: "0.8rem"
                    }}>
                      {resource.type || "Link"}
                    </span>
                  </a>
                ))}
              </div>
            ) : (
              <p style={{ color: "#aaa", textAlign: "center" }}>
                No additional resources for this lesson.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Notes Section */}
      {activeSection === "notes" && (
        <div data-testid="notes-section">
          <div className="card" style={{ background: "#1a1a1a" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
              <h3 style={{ color: "#FFD700", margin: 0 }}>
                ✏️ My Study Notes
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {noteSaved && (
                  <span style={{ color: "#90EE90", fontSize: "0.9rem" }}>
                    ✓ Saved!
                  </span>
                )}
                <button
                  onClick={saveNotes}
                  style={{
                    background: "#90EE90",
                    color: "#000",
                    padding: "8px 20px",
                    borderRadius: "8px",
                    marginTop: 0,
                    fontSize: "0.9rem"
                  }}
                >
                  💾 Save Notes
                </button>
              </div>
            </div>
            
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Write your notes here... These will be saved locally on your device."
              style={{
                width: "100%",
                minHeight: "300px",
                padding: "15px",
                background: "#0a0a0a",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "#eee",
                fontSize: "1rem",
                lineHeight: "1.8",
                resize: "vertical"
              }}
            />
            
            <p style={{ color: "#999", fontSize: "0.85rem", marginTop: "10px", marginBottom: 0 }}>
              💡 Tip: Notes are saved locally in your browser and will persist between sessions.
            </p>
          </div>
        </div>
      )}

      {/* Bottom Action Bar
          Emergent preview badge sits bottom-right at ~75px above the viewport
          bottom. Lifting the sticky bar by 90px guarantees it never covers
          any action button on mobile. In production (no badge) this creates
          a consistent bottom safe-area, matching the InvisibleTeacher FAB
          offset (bottom: 70px). */}
      <div className="card" style={{ 
        background: "#0a0a0a", 
        position: "sticky", 
        bottom: "90px",
        borderTop: "2px solid #333"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px", paddingRight: "8px" }}>
          <div style={{ color: "#aaa", display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
            <span>
              <span style={{ color: "#FFD700", fontWeight: "bold" }}>{getProgressPercentage()}%</span> of lesson completed
            </span>
          </div>
          {/* 2026-06-05: Standardized lesson action bar — EXACTLY 5
              buttons per user spec, identical across K-12 Academic
              section and Courses section (both render this same
              LessonWorkspace). Order locked: Back, Restart, Take Quiz,
              Next, Download PDF. */}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              onClick={onBack}
              data-testid="back-to-curriculum-btn"
              style={{
                background: "#333",
                padding: "12px 25px",
                marginTop: 0
              }}
            >
              ← Back to Curriculum
            </button>
            <button
              onClick={handleRestartLesson}
              data-testid="restart-lesson-btn"
              title="Clear progress and start this lesson from the beginning"
              style={{
                background: "linear-gradient(135deg, #4a2a2a 0%, #6a3a3a 100%)",
                color: "#fff",
                padding: "12px 25px",
                fontWeight: "bold",
                marginTop: 0,
                border: "1px solid #FFA07A"
              }}
            >
              🔄 Restart Lesson
            </button>
            <button
              onClick={() => onTakeQuiz && onTakeQuiz(lessonNumber)}
              disabled={!onTakeQuiz}
              data-testid="take-quiz-from-workspace"
              style={{
                background: onTakeQuiz ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)" : "#2a2a2a",
                color: onTakeQuiz ? "#000" : "#888",
                padding: "12px 30px",
                fontWeight: "bold",
                marginTop: 0,
                cursor: onTakeQuiz ? "pointer" : "not-allowed"
              }}
            >
              📝 Take Lesson Quiz
            </button>
            {nextLesson && onNextLesson && (
              <button
                onClick={() => onNextLesson(nextLesson)}
                data-testid="next-lesson-btn"
                title={`Next: ${nextLesson.title || ''}`}
                style={{
                  background: "linear-gradient(135deg, #1a4d1a 0%, #2a6d2a 100%)",
                  color: "#fff",
                  padding: "12px 25px",
                  fontWeight: "bold",
                  marginTop: 0,
                  border: "1px solid #90EE90"
                }}
              >
                Next Lesson →
              </button>
            )}
            <button
              onClick={downloadPDF}
              disabled={pdfLoading}
              data-testid="download-pdf-btn"
              title="Download this lesson as a printable PDF"
              style={{
                background: pdfLoading ? "#2a2a2a" : "linear-gradient(135deg, #3a3a1a 0%, #5a4a1a 100%)",
                color: pdfLoading ? "#888" : "#FFD700",
                padding: "12px 25px",
                marginTop: 0,
                fontWeight: "bold",
                border: "1px solid #FFD700",
                cursor: pdfLoading ? "wait" : "pointer"
              }}
            >
              {pdfLoading ? "Building PDF…" : "📄 Download PDF"}
            </button>
          </div>
        </div>
      </div>
      </div>
      
      {/* ZERO-BOX PROTOCOL: ClassroomSidebar REMOVED - No chat bubbles allowed */}
      
      {/* THE INVISIBLE TEACHER - Voice-First AI */}
      {content && (
        <InvisibleTeacher
          key={`it-${lessonNumber}-${restartKey}`}
          gradeLevel={gradeLevel}
          lessonContent={
            content.introduction + '\n\n' + 
            (content.sections?.map(s => `${s.title}\n${s.content}`).join('\n\n') || '')
          }
          lessonTitle={lessonTitle || content.title}
          autoStart={isYoungLearnerGrade(gradeLevel)}
          // 2026-02-11c: Broadcast the AI's current section so the
          // page can scroll the matching section into view — keeps
          // visible content in lock-step with the audio narration.
          onSectionStart={(idx) => {
            try {
              window.dispatchEvent(
                new CustomEvent("ylc:teacher-section-start", { detail: { idx } })
              );
            } catch {
              /* non-fatal */
            }
          }}
          onLessonComplete={() => {
            setLessonComplete(true);
            // Auto-scroll to the bottom CTA row so the "Next Lesson →"
            // button (line ~932) is immediately visible.
            setTimeout(() => {
              const cta = document.querySelector('[data-testid="next-lesson-btn"]')
                || document.querySelector('[data-testid="take-quiz-from-workspace"]');
              cta?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 500);
          }}
        />
      )}

      {/* Lesson-complete celebration banner — appears after the teacher
          finishes the outro. Sticky at the top so the student sees the
          next-lesson CTA even mid-scroll. */}
      {lessonComplete && (
        <div
          data-testid="lesson-complete-banner"
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9998,
            background: "linear-gradient(135deg, #1a4d1a 0%, #2a6d2a 100%)",
            color: "#fff",
            padding: "12px 22px",
            borderRadius: "999px",
            boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
            border: "1px solid #90EE90",
            display: "flex",
            alignItems: "center",
            gap: "14px",
            maxWidth: "calc(100vw - 40px)",
          }}
        >
          <span style={{ fontSize: "1.4rem" }}>🎉</span>
          <span style={{ fontWeight: "bold", fontSize: "0.9rem" }}>
            Lesson Complete!
          </span>
          {nextLesson && onNextLesson && (
            <button
              data-testid="lesson-complete-next-btn"
              onClick={() => {
                setLessonComplete(false);
                onNextLesson(nextLesson);
              }}
              style={{
                background: "#FFD700",
                color: "#000",
                border: "none",
                borderRadius: "999px",
                padding: "6px 14px",
                fontSize: "0.82rem",
                fontWeight: "bold",
                cursor: "pointer",
                marginTop: 0,
                width: "auto",
              }}
            >
              Next Lesson →
            </button>
          )}
          <button
            onClick={() => setLessonComplete(false)}
            aria-label="Dismiss"
            style={{
              background: "transparent",
              color: "#fff",
              border: "none",
              fontSize: "1.1rem",
              cursor: "pointer",
              padding: "0 4px",
              marginTop: 0,
              width: "auto",
            }}
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
}

export default LessonWorkspace;
